import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { t } from '@lingui/macro';

import Person from '@rover/icons/svg/person.svg';
import { Box, Button } from '@rover/kibble/core';
import { SM_MIN } from '@rover/kibble/styles/MediaQuery';
import useMatchMedia from '@rover/react-lib/src/hooks/useMatchMedia';
import { selectors as searchFiltersSelectors } from '@rover/react-lib/src/pages/search/SearchPage/SearchFilters.duck';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import useDucks from '@rover/shared/js/hooks/useDucks';

import { useSearchByNameExperiment } from './hooks/useSearchByNameExperiment';
import { SearchNameTabSelectedEvent } from './analyticsEvents';
import { SEARCH_BY_NAME_OPEN_PARAM } from './constants';
import SearchByNameOverlay from './SearchByNameOverlay';

const SearchByNameButton = (): JSX.Element | null => {
  const { isEnabled } = useSearchByNameExperiment();
  // SBN is desktop-only: the overlay returns null on mobile, so the button
  // mustn't render there either or it'd be a click-to-nothing dead end.
  const isDesktop = useMatchMedia(`(min-width:${SM_MIN}px)`);
  const history = useHistory();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const { data } = useDucks({
    selector: (state) => ({
      searchLocation: searchFiltersSelectors.getFilters(state).location ?? '',
    }),
  });

  const stripOpenParam = useCallback((): void => {
    const params = new URLSearchParams(location.search);
    if (!params.has(SEARCH_BY_NAME_OPEN_PARAM)) return;
    params.delete(SEARCH_BY_NAME_OPEN_PARAM);
    const nextSearch = params.toString();
    // replace (not push) so the stale param doesn't survive the back button.
    history.replace({
      pathname: location.pathname,
      search: nextSearch ? `?${nextSearch}` : '',
      hash: location.hash,
    });
  }, [history, location.hash, location.pathname, location.search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const hasOpenParam = params.get(SEARCH_BY_NAME_OPEN_PARAM) === 'true';

    if (!isEnabled) {
      if (hasOpenParam) stripOpenParam();
      return;
    }

    // Param is preserved while open — wizard-suppression depends on it; stripped on close.
    if (hasOpenParam) setIsOpen(true);
  }, [isEnabled, location.search, stripOpenParam]);

  const handleOpen = useCallback((): void => {
    setIsOpen(true);
    emitAnalyticsEvent(new SearchNameTabSelectedEvent({ tab: 'name' }));
  }, []);

  const handleClose = useCallback((): void => {
    setIsOpen(false);
    stripOpenParam();
  }, [stripOpenParam]);

  if (!isEnabled || !isDesktop) return null;

  return (
    <>
      <Box flexShrink="0">
        <Button
          variant="default"
          icon={Person}
          onClick={handleOpen}
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          testID="search-by-name-button"
        >
          {t`Search by name`}
        </Button>
      </Box>
      <SearchByNameOverlay
        isOpen={isOpen}
        onClose={handleClose}
        initialLocation={data.searchLocation}
      />
    </>
  );
};

export default SearchByNameButton;
